/**
 * KeyboardShortcuts. Mounted once in the frame. Owns every single-key
 * shortcut in the command centre and the sheet that lists them.
 *
 *   g then d / l / h / b / r / s / t / x   go to a destination (role-filtered)
 *   n                                      the page's primary action
 *   ?                                      this sheet
 *   Ctrl K / Cmd K                         command palette (owned by its host)
 *
 * Keys are ignored while typing, while a modifier is held, and while any
 * dialog, drawer, palette or menu is open, so a shortcut never fires behind
 * an overlay.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/lib/tenant";
import { paletteShortcutLabel } from "@/lib/commandPalette";
import {
  OPEN_SHORTCUTS_EVENT,
  PRIMARY_ACTION_EVENT,
  isModalOpen,
  isTypingTarget,
} from "@/lib/shortcuts";
import { labelFor, visibleNav } from "@/primitives/layout/frame/nav";
import "@/primitives/overlays/commandPalette.css";
import "./shortcuts.css";

const CHORD_WINDOW = 1200;

export function KeyboardShortcuts() {
  const { user } = useAuth();
  const { features } = useTenant();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const pendingG = useRef<number>(0);

  useEffect(() => {
    const onOpen = () => setSheetOpen(true);
    window.addEventListener(OPEN_SHORTCUTS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SHORTCUTS_EVENT, onOpen);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target) || isModalOpen()) return;

      const key = e.key;
      const now = Date.now();

      if (pendingG.current && now - pendingG.current < CHORD_WINDOW) {
        pendingG.current = 0;
        const dest = visibleNav(user?.role, features).find((n) => n.key === key.toLowerCase());
        if (dest) {
          e.preventDefault();
          navigate(dest.to);
        }
        return;
      }

      if (key === "g") {
        pendingG.current = now;
        return;
      }
      if (key === "?") {
        e.preventDefault();
        setSheetOpen(true);
        return;
      }
      if (key === "n") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(PRIMARY_ACTION_EVENT));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, user?.role, features]);

  return <ShortcutSheet open={sheetOpen} onClose={closeSheet} />;
}

function ShortcutSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const reduce = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => closeRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === "Tab") {
        // One focusable control: keep focus inside the sheet.
        e.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open, onClose]);

  const { features } = useTenant();
  const nav = visibleNav(user?.role, features);
  const general: Array<{ keys: string[]; label: string }> = [
    { keys: [paletteShortcutLabel()], label: "Search or run an action" },
    { keys: ["n"], label: "Primary action on this page" },
    { keys: ["?"], label: "Show these shortcuts" },
    { keys: ["Esc"], label: "Close a dialog, drawer or menu" },
  ];

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <Fragment key="ks">
          <motion.div
            className="cp-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.16 }}
            onClick={onClose}
            aria-hidden
          />
          <div className="cp-wrap">
            <motion.div
              className="cp ks"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ks-title"
              initial={{ opacity: 0, y: reduce ? 0 : -8, scale: reduce ? 1 : 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: reduce ? 0 : -6, scale: reduce ? 1 : 0.985 }}
              transition={{ duration: reduce ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="ks-head">
                <h2 id="ks-title" className="st-title" style={{ fontSize: 18 }}>
                  Keyboard shortcuts
                </h2>
                <button ref={closeRef} type="button" className="ks-close" aria-label="Close shortcuts" onClick={onClose}>
                  <X size={18} strokeWidth={1.75} />
                </button>
              </div>
              <div className="ks-body">
                <section className="ks-col" aria-labelledby="ks-general">
                  <p id="ks-general" className="st-label ks-group">
                    General
                  </p>
                  <dl className="ks-list">
                    {general.map((row) => (
                      <div key={row.label} className="ks-row">
                        <dt className="ks-label">{row.label}</dt>
                        <dd className="ks-keys">
                          {row.keys.map((k) => (
                            <kbd key={k}>{k}</kbd>
                          ))}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
                <section className="ks-col" aria-labelledby="ks-goto">
                  <p id="ks-goto" className="st-label ks-group">
                    Go to
                  </p>
                  <dl className="ks-list">
                    {nav.map((n) => (
                      <div key={n.to} className="ks-row">
                        <dt className="ks-label">{labelFor(n, user?.role)}</dt>
                        <dd className="ks-keys">
                          <kbd>g</kbd>
                          <span className="ks-then">then</span>
                          <kbd>{n.key}</kbd>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </div>
            </motion.div>
          </div>
        </Fragment>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
